import { gsap } from 'gsap';
import { getElementFromEvent } from '../utils';
import global from '../globalvars';

export default class Menu {
	constructor(el) {
		this.header = el;

		this.hamburger = this.header.querySelector('[data-hamburger]');
		this.slideOut = this.header.closest('[data-barba]').querySelector('[data-slide-out-menu]');
		this.menu = this.header.querySelector('[data-menu]');
		this.menuList = this.header.querySelector('[data-menu-list]');
		this.menuItems = this.menu.querySelectorAll('[data-menu-item]');

		this.headerBanner = this.header.querySelector('[data-header-banner]');
		this.bannerImage = this.header.querySelector('[data-banner-image]');
		this.bannerContent = this.header.querySelector('[data-banner-content]');
		this.bannerCloseButton = this.header.querySelector('[data-banner-close]');
		this.bannerHidden = false;

		if (sessionStorage.getItem('bannerHidden') === 'true' && this.headerBanner) {
			this.headerBanner.style.display = 'none';
		} else if (this.headerBanner && this.bannerCloseButton) {
			this.bannerCloseButton.addEventListener('click', () => {
				this.hideBanner();
			});
		}

		if (!this.menuItems) {
			return;
		}

		if (this.headerBanner) {
			this.menu.style.setProperty('--bannerHeight', this.headerBanner.offsetHeight + 'px');
		} else {
			this.menu.style.setProperty('--bannerHeight', '0px');
		}

		this.breakpoint = window.matchMedia('(max-width:1200px)');

		this.slideOutOpen = false;
		this.isAnimating = false;

		if (this.breakpoint.matches) {
			// Mobile

			this.menuList.addEventListener('click', (e) => {
				const button = getElementFromEvent(e, '[data-dropdown-toggle]');

				if (!button) {
					return;
				}
				const parent = button.closest('[data-menu-item]');

				this.menuItems.forEach((element) => {
					const dropdown = element.querySelector('.c-header-menu__dropdown-wrapper');

					if (parent == element) {
						if (parent.classList.contains('is-active')) {
							element.classList.remove('is-active');
							dropdown.style.height = 0;
						} else {
							element.classList.add('is-active');
							dropdown.style.height = dropdown.scrollHeight + 32 + 'px';
						}
					} else {
						element.classList.remove('is-active');
						dropdown.style.height = 0;
					}
				});
			});
		} else {
			// Desktop

			this.expandedItem = null;

			this.menuItems.forEach((item) => {
				const button = item.querySelector('button');

				button.addEventListener('click', (event) => {
					if (button.ariaExpanded === 'false') {
						this.expandSubMenu(item);
					} else {
						this.collapseSubMenu(item);
					}
				});

				item.addEventListener('mouseenter', () => {
					this.expandSubMenu(item);
				});
				item.addEventListener('mouseleave', () => {
					this.collapseSubMenu(item);
				});

				// Handling keyboard navigation
				button.addEventListener('keydown', (event) => {
					if (event.key === 'Enter' || event.key === ' ') {
						// Space or Enter key
						event.preventDefault(); // Prevent the default action to stop scrolling when pressing Space
						if (button.ariaExpanded === 'false') {
							this.expandSubMenu(item);
						} else {
							this.collapseSubMenu(item);
						}
					}
				});

				// Handling tab key inside submenu to loop back to the button
				const subMenuLinks = item.querySelectorAll('ul a');
				if (subMenuLinks.length) {
					const lastLink = subMenuLinks[subMenuLinks.length - 1];
					lastLink.addEventListener('keydown', (event) => {
						if (event.key === 'Tab' && !event.shiftKey) {
							event.preventDefault();
							button.focus(); // Move focus back to the button
						}
					});
				}
			});
		}

		this.hamburger.addEventListener('click', (e) => {
			this.toggleMenu();
		});

		let focusableElements = this.menu.querySelectorAll('li');

		this.firstTarget = focusableElements[0];
		this.lastTarget = focusableElements[focusableElements.length - 1];

		this.firstFocusableEl = this.firstTarget.firstElementChild;
		this.lastFocusableEl = this.lastTarget.firstElementChild;

		window.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') {
				if (this.slideOutOpen) {
					this.closeMenu();
				}
			}

			if (e.key === 'Tab' || e.key === 9) {
				if (this.slideOutOpen) {
					if (e.shiftKey) {
						/* shift + tab */ if (document.activeElement === this.firstFocusableEl) {
							this.lastFocusableEl.focus();
							e.preventDefault();
						}
					} /* tab */ else {
						if (document.activeElement === this.lastFocusableEl) {
							this.firstFocusableEl.focus();
							e.preventDefault();
						}
					}
				}
			}
		});
	}

	toggleMenu() {
		this.slideOutOpen ? this.closeMenu() : this.openMenu();
	}

	openMenu() {
		if (this.isAnimating || this.slideOutOpen) {
			return;
		}

		this.slideOutOpen = true;
		this.isAnimating = true;

		// ScrollLock Disable
		window.dispatchEvent(new CustomEvent('disable-scroll'));

		this.hamburger.classList.add('is-active');
		this.hamburger.setAttribute('aria-expanded', true);

		gsap.set(this.header, {
			zIndex: 9999,
		});

		gsap.set(this.slideOut, {
			visibility: 'visible',
		});

		const tl = gsap.timeline({
			ease: 'sine.inOut',
			duration: 0.5,
			onComplete: () => {
				this.isAnimating = false;
				this.firstFocusableEl.focus();
			},
		});

		if (this.breakpoint.matches === true) {
			if (this.headerBanner) {
				tl.set(this.headerBanner, {
					scaleY: 0,
					transformOrigin: 'top',
					onComplete: () => {
						this.headerBanner.style.display = 'none';
						this.menu.style.setProperty('--bannerHeight', '0px');
					},
				});
			}
			tl.to(this.slideOut, {
				xPercent: -100,
			});
			tl.to(this.menu, {
				opacity: 1,
			});
		}
	}

	closeMenu() {
		if (this.isAnimating || !this.slideOutOpen) {
			return;
		}

		this.isAnimating = true;
		this.hamburger.classList.remove('is-active');

		const tl = gsap.timeline({
			ease: 'sine.inOut',
			duration: 0.5,
			onComplete: () => {
				this.slideOutOpen = false;
				this.isAnimating = false;

				gsap.set(this.slideOut, {
					visibility: 'hidden',
				});

				gsap.set(this.header, {
					zIndex: 999,
				});

				// ScrollLock Enable
				window.dispatchEvent(new CustomEvent('enable-scroll'));

				this.hamburger.setAttribute('aria-expanded', false);
				this.hamburger.focus();
			},
		});

		if (this.breakpoint.matches === true) {
			tl.to(this.menu, {
				opacity: 0,
			});
			tl.to(this.slideOut, {
				xPercent: 0,
			});
			if (this.headerBanner) {
				tl.to(this.headerBanner, {
					scaleY: 1,
					transformOrigin: 'top',
					onComplete: () => {
						this.headerBanner.style.display = 'flex';
						this.menu.style.setProperty('--bannerHeight', this.headerBanner.offsetHeight + 'px');
					},
				});
			}
		}
	}

	expandSubMenu(item) {
		if (this.timer) {
			clearTimeout(this.timer);
			this.timer = null;
		}

		const subMenu = item.querySelector('.c-header-menu__dropdown-wrapper');

		if (!subMenu) {
			return;
		}

		const button = item.querySelector('[data-dropdown-toggle]');
		this.expandedItem = item;

		this.header.classList.add('has-expanded-menu');

		subMenu.setAttribute('aria-hidden', 'false');
		button.setAttribute('aria-expanded', 'true');
		item.dataset.expanded = 'true';
		subMenu.querySelectorAll('a')[0].focus(); // Focus on the first link in the submenu
	}

	collapseSubMenu(item) {
		const subMenu = item.querySelector('.c-header-menu__dropdown-wrapper');

		if (!subMenu) {
			return;
		}

		const button = item.querySelector('[data-dropdown-toggle]');
		this.expandedItem = null;

		this.timer = setTimeout(() => {
			this.header.classList.remove('has-expanded-menu');
		}, 300);

		subMenu.setAttribute('aria-hidden', 'true');
		button.setAttribute('aria-expanded', 'false');
		item.dataset.expanded = 'false';
	}

	hideBanner() {
		const tl = gsap.timeline();

		tl.to([this.bannerImage, this.bannerContent, this.bannerCloseButton], {
			opacity: 0,
			duration: 0.3,
			ease: 'power2.inOut',
		});

		tl.to(
			this.headerBanner,
			{
				scaleY: 0,
				transformOrigin: 'top',
				duration: 0.5,
				ease: 'power2.inOut',
				onComplete: () => {
					sessionStorage.setItem('bannerHidden', 'true');
					this.menu.style.setProperty('--bannerHeight', '0px');
					this.headerBanner.remove();
				},
			},
			0.3
		);
	}
}
